import { Controller } from '@hotwired/stimulus';
import { type ModalController } from './modal';

/**
 * FoodDiaryController handles the food diary page interactions.
 *
 * Features:
 * - Delete meal confirmation
 * - Delete day confirmation
 * - Event propagation control for nested interactive elements
 */
export class FoodDiaryController extends Controller {
    static outlets = ['modal'];
    declare readonly modalOutlets: ModalController[];
    static targets = [
        'waterSubmitLabel',
        'waterSubmitLoading',
        'waterAmountSelect',
        'waterAmountCustomWrap',
        'waterAmountCustomInput',
        'exerciseTypeSelect',
        'exerciseCustomWrap',
        'exerciseCustomInput',
    ];
    declare readonly hasWaterSubmitLabelTarget: boolean;
    declare readonly hasWaterSubmitLoadingTarget: boolean;
    declare readonly hasWaterAmountSelectTarget: boolean;
    declare readonly hasWaterAmountCustomWrapTarget: boolean;
    declare readonly hasWaterAmountCustomInputTarget: boolean;
    declare readonly hasExerciseTypeSelectTarget: boolean;
    declare readonly hasExerciseCustomWrapTarget: boolean;
    declare readonly hasExerciseCustomInputTarget: boolean;
    declare readonly waterSubmitLabelTarget: HTMLElement;
    declare readonly waterSubmitLoadingTarget: HTMLElement;
    declare readonly waterAmountSelectTarget: HTMLSelectElement;
    declare readonly waterAmountCustomWrapTarget: HTMLElement;
    declare readonly waterAmountCustomInputTarget: HTMLInputElement;
    declare readonly exerciseTypeSelectTarget: HTMLSelectElement;
    declare readonly exerciseCustomWrapTarget: HTMLElement;
    declare readonly exerciseCustomInputTarget: HTMLInputElement;
    private pendingDeleteMealForm: HTMLFormElement | null = null;
    private pendingDeleteDayForm: HTMLFormElement | null = null;
    private readonly confirmedForms = new WeakSet<HTMLFormElement>();
    private readonly handleDeleteMealConfirmedBound = this.handleDeleteMealConfirmed.bind(this);
    private readonly handleDeleteDayConfirmedBound = this.handleDeleteDayConfirmed.bind(this);
    private dragCard: HTMLElement | null = null;
    private dragPlaceholder: HTMLDivElement | null = null;
    private dragPointerId: number | null = null;
    private dragPointerOffsetY = 0;
    private dragOriginalOrder: string[] = [];
    private dragCombineTarget: HTMLElement | null = null;
    private dragCombineTimer: number | null = null;
    private dragCombineReady = false;
    private suppressNextClick = false;
    private readonly handleDragMoveBound = this.handleDragMove.bind(this);
    private readonly handleDragEndBound = this.handleDragEnd.bind(this);

    connect(): void {
        this.syncDefaultCalendarView();
        document.addEventListener('ova:food-diary:confirm-delete-meal', this.handleDeleteMealConfirmedBound);
        document.addEventListener('ova:food-diary:confirm-delete-day', this.handleDeleteDayConfirmedBound);
        this.resetTransientState();
        this.syncWaterAmountField();
        this.syncExerciseCustomNameField();
    }

    disconnect(): void {
        document.removeEventListener('ova:food-diary:confirm-delete-meal', this.handleDeleteMealConfirmedBound);
        document.removeEventListener('ova:food-diary:confirm-delete-day', this.handleDeleteDayConfirmedBound);
        this.teardownDragState();
    }

    private getModalById(id: string): ModalController {
        const modal = this.modalOutlets.find((outlet) => outlet.element.id === id);
        if (!modal) {
            throw new Error(`Modal with id "${id}" not found`);
        }
        return modal;
    }

    private handleDeleteMealConfirmed(): void {
        if (!this.pendingDeleteMealForm) {
            return;
        }
        if (this.isDeleteFormSubmitting(this.pendingDeleteMealForm)) {
            return;
        }
        this.confirmedForms.add(this.pendingDeleteMealForm);
        this.pendingDeleteMealForm.requestSubmit();
        this.pendingDeleteMealForm = null;
    }

    private handleDeleteDayConfirmed(): void {
        if (!this.pendingDeleteDayForm) {
            return;
        }
        if (this.isDeleteFormSubmitting(this.pendingDeleteDayForm)) {
            return;
        }
        this.confirmedForms.add(this.pendingDeleteDayForm);
        this.pendingDeleteDayForm.requestSubmit();
        this.pendingDeleteDayForm = null;
    }

    private syncDefaultCalendarView(): void {
        const url = new URL(window.location.href);
        if (url.pathname !== '/food-diary' || url.searchParams.has('view')) {
            return;
        }

        if (!window.matchMedia('(min-width: 1024px)').matches) {
            return;
        }

        url.searchParams.set('view', 'month');
        window.location.replace(url.toString());
    }

    /**
     * Open the duplicate meal dialog for the selected meal
     */
    openDuplicateDialog(event: Event): void {
        const trigger = event.currentTarget as HTMLElement | null;
        const dialogId = trigger?.dataset.dialogId;
        if (!dialogId) {
            return;
        }

        const dialog = this.element.querySelector<HTMLDialogElement>(`#${dialogId}`);
        if (dialog?.showModal) {
            dialog.showModal();
        } else if (dialog) {
            dialog.classList.remove('hidden');
        }
    }

    /**
     * Close the duplicate meal dialog
     */
    closeDuplicateDialog(event: Event): void {
        const trigger = event.currentTarget as HTMLElement | null;
        const dialogId = trigger?.dataset.dialogId;
        if (!dialogId) {
            return;
        }

        const dialog = this.element.querySelector<HTMLDialogElement>(`#${dialogId}`);
        if (dialog?.open) {
            dialog.close();
        } else if (dialog) {
            dialog.classList.add('hidden');
        }
    }

    beginHandleDrag(event: PointerEvent): void {
        if (event.button !== 0 || this.dragCard) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        const handle = event.currentTarget as HTMLElement | null;
        const card = handle?.closest<HTMLElement>('[data-meal-card]');
        if (!handle || !card) {
            return;
        }

        this.activateDrag(card, handle, event.pointerId, event.clientY);
    }

    maybePreventSummaryToggle(event: Event): void {
        if (!this.suppressNextClick) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.suppressNextClick = false;
    }

    /**
     * Confirm before deleting a meal
     */
    confirmDeleteMeal(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.isDeleteFormSubmitting(form)) {
            event.preventDefault();
            return;
        }
        if (this.consumeConfirmedDeleteForm(form)) {
            this.markDeleteFormSubmitting(form);
            return;
        }
        event.preventDefault();
        this.pendingDeleteMealForm = form;
        this.getModalById('food-diary-delete-meal-modal').show();
    }

    /**
     * Confirm before deleting an entire day's entries
     */
    confirmDeleteDay(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.isDeleteFormSubmitting(form)) {
            event.preventDefault();
            return;
        }
        if (this.consumeConfirmedDeleteForm(form)) {
            this.markDeleteFormSubmitting(form);
            return;
        }
        event.preventDefault();
        this.pendingDeleteDayForm = form;
        this.getModalById('food-diary-delete-day-modal').show();
    }

    submitWaterForm(event: Event): void {
        const form = event.currentTarget as HTMLFormElement | null;
        if (!form) {
            return;
        }

        const submitter = ((event as SubmitEvent).submitter as HTMLButtonElement | null)
            ?? form.querySelector<HTMLButtonElement>('button[type="submit"]');
        const label = form.querySelector<HTMLElement>('[data-food-diary-target="waterSubmitLabel"]');
        const loading = form.querySelector<HTMLElement>('[data-food-diary-target="waterSubmitLoading"]');

        if (submitter) {
            submitter.dataset.turboResetDisabled = 'true';
            submitter.disabled = true;
            submitter.classList.add('opacity-75', 'cursor-not-allowed');
        }
        form.setAttribute('aria-busy', 'true');

        this.replaceButtonLabelWithLoading(label, loading);
    }

    waterPresetChanged(): void {
        this.syncWaterAmountField();
    }

    exerciseTypeChanged(): void {
        this.syncExerciseCustomNameField();
    }

    /**
     * Stop event propagation to prevent parent elements from handling the event
     * Used for delete buttons inside collapsible details elements
     */
    stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    showFeelingNote(event: Event): void {
        const input = event.currentTarget as HTMLInputElement | null;
        const form = input?.closest('form');
        const notePanel = form?.querySelector<HTMLElement>('[data-feeling-note-panel]');
        notePanel?.classList.remove('hidden');
    }

    openDashboardCard(event: Event): void {
        const trigger = event.currentTarget as HTMLElement | null;
        const selector = trigger?.dataset.dashboardTarget || trigger?.getAttribute('href');
        if (!selector?.startsWith('#')) {
            return;
        }

        event.preventDefault();
        const target = this.element.querySelector<HTMLElement>(selector);
        if (!target) {
            return;
        }

        if (trigger?.dataset.dashboardOpen !== 'false') {
            const panel = target.matches('details')
                ? target as HTMLDetailsElement
                : target.querySelector<HTMLDetailsElement>('details');
            if (panel) {
                panel.open = true;
            }
        }

        window.history.replaceState(null, '', selector);
        target.scrollIntoView({behavior: 'smooth', block: 'start'});

        const focusTarget = target.querySelector<HTMLElement>('summary, a, button, input, textarea, select');
        window.setTimeout(() => focusTarget?.focus({preventScroll: true}), 250);
    }

    submitFeelingAnalysisForm(event: Event): void {
        const form = event.currentTarget as HTMLFormElement | null;
        if (!form) {
            return;
        }

        const submitter = ((event as SubmitEvent).submitter as HTMLButtonElement | null)
            ?? form.querySelector<HTMLButtonElement>('button[type="submit"]');
        const label = form.querySelector<HTMLElement>('[data-feeling-analysis-label]');
        const loading = form.querySelector<HTMLElement>('[data-feeling-analysis-loading]');

        if (submitter) {
            submitter.dataset.turboResetDisabled = 'true';
            submitter.disabled = true;
            submitter.classList.add('opacity-75', 'cursor-not-allowed');
        }
        form.setAttribute('aria-busy', 'true');

        this.replaceButtonLabelWithLoading(label, loading);
    }

    private replaceButtonLabelWithLoading(label: HTMLElement | null, loading: HTMLElement | null): void {
        if (label) {
            label.dataset.turboResetLabel = 'true';
            label.classList.add('hidden');
            label.style.display = 'none';
        }

        if (loading) {
            loading.dataset.turboResetLoading = 'true';
            loading.classList.remove('hidden');
            loading.classList.add('inline-flex');
            loading.style.display = 'inline-flex';
        }
    }

    private markDeleteFormSubmitting(form: HTMLFormElement): void {
        form.dataset.deleteSubmitting = 'true';
        form.setAttribute('aria-busy', 'true');

        const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (submitButton) {
            submitButton.dataset.turboResetDisabled = 'true';
            submitButton.disabled = true;
            submitButton.classList.add('opacity-75', 'cursor-not-allowed');
        }
    }

    private isDeleteFormSubmitting(form: HTMLFormElement): boolean {
        return form.dataset.deleteSubmitting === 'true';
    }

    private consumeConfirmedDeleteForm(form: HTMLFormElement): boolean {
        if (!this.confirmedForms.has(form)) {
            return false;
        }
        this.confirmedForms.delete(form);
        return true;
    }

    private resetTransientState(): void {
        for (const form of this.element.querySelectorAll<HTMLFormElement>('form[aria-busy="true"]')) {
            form.removeAttribute('aria-busy');
            delete form.dataset.deleteSubmitting;
        }

        for (const button of this.element.querySelectorAll<HTMLButtonElement>('button[data-turbo-reset-disabled]')) {
            button.disabled = false;
            button.classList.remove('opacity-75', 'cursor-not-allowed');
            if (button.dataset.originalText) {
                button.textContent = button.dataset.originalText;
            }
            delete button.dataset.turboResetDisabled;
        }

        for (const loading of this.element.querySelectorAll<HTMLElement>('[data-turbo-reset-loading]')) {
            loading.classList.add('hidden');
            loading.classList.remove('inline-flex');
            loading.style.removeProperty('display');
            delete loading.dataset.turboResetLoading;
        }

        for (const label of this.element.querySelectorAll<HTMLElement>('[data-turbo-reset-label]')) {
            label.classList.remove('hidden');
            label.style.removeProperty('display');
            delete label.dataset.turboResetLabel;
        }

        for (const dialog of this.element.querySelectorAll<HTMLDialogElement>('dialog[open]')) {
            dialog.close();
        }
    }

    private syncWaterAmountField(): void {
        if (!this.hasWaterAmountSelectTarget || !this.hasWaterAmountCustomWrapTarget || !this.hasWaterAmountCustomInputTarget) {
            return;
        }

        const isCustom = this.waterAmountSelectTarget.value === 'custom';
        this.waterAmountCustomWrapTarget.classList.toggle('hidden', !isCustom);
        this.waterAmountCustomInputTarget.disabled = !isCustom;
        this.waterAmountCustomInputTarget.required = isCustom;

        if (!isCustom) {
            this.waterAmountCustomInputTarget.value = '';
        }
    }

    private syncExerciseCustomNameField(): void {
        if (!this.hasExerciseTypeSelectTarget || !this.hasExerciseCustomWrapTarget || !this.hasExerciseCustomInputTarget) {
            return;
        }

        const isOther = this.exerciseTypeSelectTarget.value === 'other';
        this.exerciseCustomWrapTarget.classList.toggle('hidden', !isOther);
        this.exerciseCustomInputTarget.disabled = !isOther;
        this.exerciseCustomInputTarget.required = isOther;

        if (!isOther) {
            this.exerciseCustomInputTarget.value = '';
        }
    }

    private activateDrag(card: HTMLElement, handle: HTMLElement, pointerId: number, pointerY: number): void {
        const mealList = this.getMealList();
        if (!mealList) {
            return;
        }

        const rect = card.getBoundingClientRect();
        this.dragOriginalOrder = this.getMealCards().map((mealCard) => mealCard.dataset.mealId ?? '');
        this.dragCard = card;
        this.dragPointerId = pointerId;
        this.dragPointerOffsetY = pointerY - rect.top;
        this.suppressNextClick = true;

        const placeholder = document.createElement('div');
        placeholder.className = 'food-diary-meal-placeholder rounded-lg border-2 border-dashed border-green-300 bg-green-50/70';
        placeholder.style.height = `${rect.height}px`;
        this.dragPlaceholder = placeholder;

        card.after(placeholder);
        card.classList.add('food-diary-meal-card--ready', 'food-diary-meal-card--dragging');
        card.style.width = `${rect.width}px`;
        card.style.height = `${rect.height}px`;
        card.style.left = `${rect.left}px`;
        card.style.top = `${rect.top}px`;
        document.body.classList.add('food-diary-drag-active');
        mealList.classList.add('food-diary-meal-list--sorting');
        handle.setPointerCapture?.(pointerId);

        document.addEventListener('pointermove', this.handleDragMoveBound);
        document.addEventListener('pointerup', this.handleDragEndBound);
        document.addEventListener('pointercancel', this.handleDragEndBound);
    }

    private handleDragMove(event: PointerEvent): void {
        if (!this.dragCard || !this.dragPlaceholder || this.dragPointerId !== event.pointerId) {
            return;
        }

        event.preventDefault();

        const nextTop = event.clientY - this.dragPointerOffsetY;
        this.dragCard.style.top = `${nextTop}px`;
        this.updateDragCombineTarget(event.clientX, event.clientY);

        const cards = this.getMealCards().filter((card) => card !== this.dragCard);
        let inserted = false;
        for (const card of cards) {
            const rect = card.getBoundingClientRect();
            const midpoint = rect.top + (rect.height / 2);
            if (event.clientY < midpoint) {
                card.before(this.dragPlaceholder);
                inserted = true;
                break;
            }
        }

        if (!inserted) {
            this.getMealList()?.append(this.dragPlaceholder);
        }
    }

    private handleDragEnd(event: PointerEvent): void {
        if (this.dragPointerId !== event.pointerId) {
            return;
        }

        if (!this.dragCard || !this.dragPlaceholder) {
            this.teardownDragState();
            return;
        }

        const sourceMealId = this.dragCard.dataset.mealId ?? '';
        const targetMealId = this.dragCombineReady ? (this.dragCombineTarget?.dataset.mealId ?? '') : '';
        if (sourceMealId && targetMealId && sourceMealId !== targetMealId) {
            this.dragPlaceholder.before(this.dragCard);
            this.teardownDragState();
            this.submitMealCombine(sourceMealId, targetMealId);
        } else {
            this.dragPlaceholder.before(this.dragCard);
            this.teardownDragState();
            this.submitReorderedMealsIfChanged();
        }
        window.setTimeout(() => {
            this.suppressNextClick = false;
        }, 250);
    }

    private teardownDragState(): void {
        document.removeEventListener('pointermove', this.handleDragMoveBound);
        document.removeEventListener('pointerup', this.handleDragEndBound);
        document.removeEventListener('pointercancel', this.handleDragEndBound);
        document.body.classList.remove('food-diary-drag-active');

        const mealList = this.getMealList();
        mealList?.classList.remove('food-diary-meal-list--sorting');
        this.clearDragCombineTarget();

        if (this.dragCard) {
            this.dragCard.classList.remove('food-diary-meal-card--ready', 'food-diary-meal-card--dragging');
            this.dragCard.style.removeProperty('width');
            this.dragCard.style.removeProperty('height');
            this.dragCard.style.removeProperty('left');
            this.dragCard.style.removeProperty('top');
        }

        this.dragPlaceholder?.remove();
        this.dragCard = null;
        this.dragPlaceholder = null;
        this.dragPointerId = null;
        this.dragPointerOffsetY = 0;
        this.dragCombineReady = false;
    }

    private updateDragCombineTarget(pointerX: number, pointerY: number): void {
        if (!this.dragCard) {
            this.clearDragCombineTarget();
            return;
        }

        const hoveredElement = document.elementFromPoint(pointerX, pointerY);
        const target = hoveredElement?.closest<HTMLElement>('[data-meal-card]') ?? null;
        if (!target || target === this.dragCard) {
            this.clearDragCombineTarget();
            return;
        }

        if (target === this.dragCombineTarget) {
            return;
        }

        this.clearDragCombineTarget();
        this.dragCombineTarget = target;
        this.dragCard.classList.add('food-diary-meal-card--combine-over');
        target.classList.add('food-diary-meal-card--combine-candidate');
        this.dragCombineTimer = window.setTimeout(() => {
            if (this.dragCombineTarget !== target) {
                return;
            }
            this.dragCombineReady = true;
            this.dragCard?.classList.add('food-diary-meal-card--combine-over-ready');
            target.classList.add('food-diary-meal-card--combine-ready');
        }, 550);
    }

    private clearDragCombineTarget(): void {
        if (this.dragCombineTimer !== null) {
            window.clearTimeout(this.dragCombineTimer);
            this.dragCombineTimer = null;
        }

        if (this.dragCombineTarget) {
            this.dragCombineTarget.classList.remove(
                'food-diary-meal-card--combine-candidate',
                'food-diary-meal-card--combine-ready',
            );
        }

        this.dragCard?.classList.remove(
            'food-diary-meal-card--combine-over',
            'food-diary-meal-card--combine-over-ready',
        );
        this.dragCombineTarget = null;
        this.dragCombineReady = false;
    }

    private submitReorderedMealsIfChanged(): void {
        const reorderForm = this.element.querySelector<HTMLFormElement>('[data-food-diary-reorder-form]');
        const inputContainer = this.element.querySelector<HTMLElement>('[data-food-diary-reorder-inputs]');
        if (!reorderForm || !inputContainer) {
            return;
        }

        const orderedIds = this.getMealCards()
            .map((card) => card.dataset.mealId ?? '')
            .filter((value) => value.length > 0);

        if (orderedIds.length === 0 || orderedIds.join(',') === this.dragOriginalOrder.join(',')) {
            return;
        }

        inputContainer.replaceChildren();
        for (const mealId of orderedIds) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'meal_ids';
            input.value = mealId;
            inputContainer.append(input);
        }

        reorderForm.requestSubmit();
    }

    private submitMealCombine(sourceMealId: string, targetMealId: string): void {
        const combineForm = this.element.querySelector<HTMLFormElement>('[data-food-diary-combine-form]');
        const sourceInput = this.element.querySelector<HTMLInputElement>('[data-food-diary-combine-source]');
        const targetInput = this.element.querySelector<HTMLInputElement>('[data-food-diary-combine-target]');
        if (!combineForm || !sourceInput || !targetInput) {
            return;
        }

        sourceInput.value = sourceMealId;
        targetInput.value = targetMealId;
        combineForm.requestSubmit();
    }

    private getMealList(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>('[data-food-diary-meal-list]');
    }

    private getMealCards(): HTMLElement[] {
        return Array.from(this.element.querySelectorAll<HTMLElement>('[data-meal-card]'));
    }
}
